import { api } from "../axios";

import {
  FolioWiseHolding,
  SubmittedOrder,
  PaginatedResponse,
} from "../../types";
import {
  FolioNumber,
  TransactionOrder,
  PaymentMethod,
} from "../../types/transaction";

export function getFolioNumbers(
  investmentAccountId: string,
  giftOnly?: boolean
): Promise<FolioNumber[]> {
  return api.get(`folio-numbers/`, {
    params: {
      investment_account_guid: investmentAccountId,
      gift_city_flag: giftOnly,
    },
  });
}

const PAGE_SIZE = 50;

interface TransactionOrderResponse {
  orders: PaginatedResponse<TransactionOrder>;
  total_purchase_amount: number;
}

export const getTransactionOrdersApi = (
  investmentAccountId: string,
  page: string,
  pageSize: number = PAGE_SIZE
): Promise<TransactionOrderResponse> => {
  return api.get(`draft-orders/`, {
    params: {
      investment_account_guid: investmentAccountId,
      page,
      page_size: pageSize,
    },
  });
};

interface SubmitPayload {
  investment_account_guid: string;
  order_ids: number[];
  otp: string;
}

export interface SubmitResponseItem {
  payment_id: number;
  type: string;
  action_type: string;
  scheme_id: number;
  switch_out_scheme_id?: number;
  switch_in_scheme_id?: number;
  switch_out_scheme_name?: string;
  switch_in_scheme_name?: string;
  scheme_isin: string;
  scheme_name: string;
  amc_id: number;
  amc_name: string;
  amc_morningstar_code: string;
  investment_account_id: string;
  folio_id: number | null;
  amount: number | null;
  units: number | null;
  user_remarks: string | null;
  nse_reg_remark: string | null;
}

type SubmitError = {
  type: string;
  error: string;
};

export interface SubmitResponse {
  errors: SubmitError[];
  failed_orders: Array<{ order_data: SubmitResponseItem; reason: string }>;
  submitted_orders: SubmitResponseItem[];
}

export const submitTransactionOrdersApi = (
  payload: SubmitPayload
): Promise<SubmitResponse> => {
  return api.post("draft-orders/submit/", payload);
};

export const draftOrdersNotifyClientApi = (investmentAccountId: string) => {
  return api.post("draft-orders/notify_client/", {
    investment_account_guid: investmentAccountId,
  });
};

export const generateOrderVerificationOtpApi = (
  investmentAccountId: string
) => {
  return api.get("draft-orders/get_authentication_otp/", {
    params: {
      investment_account_guid: investmentAccountId,
    },
  });
};

export interface DeleteAllPayload {
  investment_account_guid: string;
  order_ids?: number[];
  delete_all?: boolean;
}

export const deleteAllTransactionOrder = (payload: DeleteAllPayload) => {
  return api.post("draft-orders/delete/", payload);
};

export interface HoldingScheme {
  scheme_isin: string;
  scheme_name: string;
  unit_balance: string;
  rupee_balance: string;
  nav: string;
  redemption_allowed: boolean;
  switch_allowed: boolean;
  stp_allowed: boolean;
  swp_allowed: boolean;
  sip_allowed: boolean;
  created_at: string;
  updated_at: string;
}

export const getHoldingSchemesApi = (investmentAccountId: string) =>
  api.get<HoldingScheme[]>("holdings/scheme-wise/", {
    params: {
      investment_account_guid: investmentAccountId,
    },
  });

export const getHoldingFoliosApi = (
  investmentAccountId: string,
  isin: string
) =>
  api.get<FolioWiseHolding[]>("holdings/folio-wise/", {
    params: {
      investment_account_guid: investmentAccountId,
      scheme_isin: isin,
    },
  });

interface GenerateOtpForTransactionPlanCancelPayload {
  investment_account_guid: string;
  plan_id: number;
  plan_type: "xsip" | "stp" | "swp";
}

export function generateOtpForTransactionPlanCancel(
  payload: GenerateOtpForTransactionPlanCancelPayload
) {
  return api.post("transaction-plan/cancel/", payload);
}

export const refetchTransactionStatusApi = (investmentAccountId: string) => {
  return api.post(`order-status-update/`, {
    investment_account_guid: investmentAccountId,
  });
};

export const fetchMFCentralTransactions = ({
  investmentAccountGuid,
  page,
  pageSize,
  isin,
}: {
  investmentAccountGuid: string;
  page: number;
  pageSize: number;
  isin: string;
}): Promise<PaginatedResponse<SubmittedOrder>> => {
  return api.get(`mfcentral/transactions/`, {
    params: {
      investment_account_guid: investmentAccountGuid,
      page,
      page_size: pageSize,
      isin,
    },
  });
};

export async function fetchTransactions({
  investment_account_guid,
  page,
  page_size,
  external,
  isin,
  folio_id,
}: {
  investment_account_guid: string;
  page: number;
  page_size: number;
  external: boolean;
  isin: string;
  folio_id?: number;
}) {
  return api.get(`consolidated/transactions/`, {
    params: {
      investment_account_guid,
      page,
      page_size,
      external,
      isin,
      folio_id,
    },
  });
}

interface CancelTransactionPayload {
  investment_account_guid: string;
  plan_id: number;
  plan_type: "XSIP" | "STP" | "SWP";
  user_remarks: string;
  user_remarks_optional?: string;
  initiated_via:
    | "mobile_app"
    | "mobile_app_android"
    | "mobile_app_ios"
    | "mobile_web"
    | "mobile_web_android"
    | "mobile_web_ios"
    | "web";
}

export const cancelTransactionApi = (payload: CancelTransactionPayload) => {
  return api.post("draft-orders/cancel/", payload);
};

interface PayOrdersPayload {
  investment_account_guid: string;
  mode_of_payment: PaymentMethod;
  order_ids: number[];
  vpa?: string;
  new_flow?: boolean;
  cheque_number?: string;
  cheque_date?: string;
  mandate_id?: number;
  bank_account_id?: number | string;
  upi_app_slug?: string;
}

export const payOrdersApi = (
  payload: PayOrdersPayload
): Promise<{
  payment: {
    nse_payment_url: string;
    payment_status: "SUCCESS" | "FAILED";
    id: string;
    nse_remark: string;
  };
}> => {
  return api.post("payments/", payload);
};

export const getPaymentStatusApi = (
  id: string | number,
  investmentAccountId: string
): Promise<{
  payment_status: "SUCCESS" | "FAILED" | "PENDING" | "REJECTED";
}> => {
  return api.get(`payments/${id}/payment_status/`, {
    params: {
      investment_account_guid: investmentAccountId,
    },
  });
};

export interface PaymentDetails {
  id: number;
  order_id: number;
  mode_of_payment: string;
  bank_name?: string;
  bank_account_name?: string;
  bank_account_number?: string;
  bank_ifsc_code?: string;
  payment_status?: string;
  nse_remark?: string;
  nse_payment_url?: string;
  nse_cart_id?: string;
  nse_reference_number?: number;
  nse_payment_reference_id?: string;
  created_at?: string;
  updated_at?: string;
  bank_account_id?: number;
  mandate_id?: number;
}

export const getPaymentsListApi = (
  investmentAccountId: string,
  orderIds: number[]
): Promise<PaymentDetails[]> => {
  return api.get(`payments/`, {
    params: {
      investment_account_guid: investmentAccountId,
      order_ids: orderIds.join(","),
    },
  });
};

export function generatePaymentLink(
  investmentAccountId: string,
  sendLink = false
): Promise<{ link: string }> {
  return api.post("payments/link/", {
    investment_account_guid: investmentAccountId,
    send_link: sendLink,
  });
}

export interface EstimateNextInstallmentPayload {
  prev_installment_date: string;
  frequency: string;
  resumption: boolean;
}

export interface EstimatedNextInstallmentDateResponse {
  installment_date: string;
}

export const getEstimatedNextInstallmentDateApi = async (
  payload: EstimateNextInstallmentPayload
): Promise<EstimatedNextInstallmentDateResponse> => {
  const response = await api.get<EstimatedNextInstallmentDateResponse>(
    "date-after-n-installments/",
    {
      params: payload,
    }
  );

  return response;
};

export function getLastUsedFolioNumbers(investmentAccountId: string): Promise<
  {
    amc_id: number;
    created_at: string;
    folio_number: string;
    id: number;
    internal_flag: boolean;
    mf_investment_account_id: string;
    modified_at: string;
  }[]
> {
  return api.get(`folio-numbers/last-used/`, {
    params: {
      investment_account_guid: investmentAccountId,
    },
  });
}
